import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEO from '../components/common/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { getTestimonialAvatars } from '../data/testimonialAvatars';
import LocalizedLink from '../components/common/LocalizedLink';

const { FiShield, FiShare2, FiClipboard, FiHeart, FiMessageCircle, FiCalendar, FiCheckCircle, FiMapPin, FiLock, FiSmartphone, FiStar, FiUsers, FiTrendingUp, FiActivity, FiClock, FiSettings, FiEye } = FiIcons;

// 動畫配置
const fadeIn = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.03,
      duration: 0.3,
      ease: "easeOut"
    }
  })
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03
    }
  }
};

const slideRight = {
  hidden: { opacity: 0, x: -15 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

const slideLeft = {
  hidden: { opacity: 0, x: 15 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut"
    }
  }
};

// 功能卡片組件
const FeatureCard = React.memo(({ icon, title, description, delay }) => {
  return (
    <motion.div
      className="bg-white p-6 rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100 h-full"
      variants={fadeIn}
      custom={delay}
    >
      <div className="text-4xl mb-4 p-3 rounded-full inline-flex items-center justify-center bg-primary-50 text-primary-500">
        <SafeIcon icon={icon} />
      </div>
      <h3 className="text-xl font-semibold mb-2 text-neutral-800">{title}</h3>
      <p className="text-neutral-600">{description}</p>
    </motion.div>
  );
});

FeatureCard.displayName = 'FeatureCard';

// 推薦卡片組件
const TestimonialCard = React.memo(({ quote, author, role, image, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="bg-white p-6 rounded-xl shadow-smooth hover:shadow-card transition-all duration-300 border border-neutral-100"
      variants={fadeIn}
      custom={delay}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="flex items-center mb-4">
        <div className="relative">
          <img 
            src={image} 
            alt={`${author} - ${role} - Pet Care Note testimonial`}
            className="w-14 h-14 rounded-full object-cover mr-4 border-2 border-primary-200"
            loading="lazy"
          />
          <div className="absolute -bottom-1 -right-1 bg-primary-500 text-white w-6 h-6 rounded-full flex items-center justify-center">
            <SafeIcon icon={FiStar} className="text-xs" />
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-neutral-800">{author}</h4>
          <p className="text-sm text-neutral-500">{role}</p>
        </div>
      </div>
      <div className="relative">
        <div className="absolute -top-3 -left-2 text-4xl text-primary-200 opacity-50">"</div>
        <p className="text-neutral-600 italic relative z-10 pl-4">{quote}</p>
        <div className="absolute -bottom-6 -right-2 text-4xl text-primary-200 opacity-50">"</div>
      </div>
    </motion.div>
  );
});

TestimonialCard.displayName = 'TestimonialCard';

// 步驟卡片組件
const StepCard = React.memo(({ number, title, description, icon, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="flex flex-col items-center text-center"
      variants={fadeIn}
      custom={delay}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="relative mb-4">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center text-white text-2xl shadow-lg">
          <SafeIcon icon={icon} />
        </div>
        <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-accent-500 text-white flex items-center justify-center text-sm font-bold shadow-md">
          {number}
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-neutral-800">{title}</h3>
      <p className="text-neutral-600">{description}</p>
    </motion.div>
  );
});

StepCard.displayName = 'StepCard';

// 標籤內容組件
const TabContent = React.memo(({ children, isActive }) => (
  <AnimatePresence mode="wait">
    {isActive && (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.15 }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
));

TabContent.displayName = 'TabContent';

// 寵物預覽卡片組件
const PetCarePreviewCard = React.memo(({ petName, petType, image, status, nextFeeding, caregiverName }) => {
  const { t } = useLanguage();
  
  return (
    <div className="glass-card p-4 w-full max-w-md shadow-lg">
      <div className="relative">
        <img 
          src={image} 
          alt={`${petName} - ${petType} pet care preview showing health tracking dashboard`}
          className="rounded-lg shadow-lg w-full object-cover h-64"
          loading="eager"
        />
        <div className="absolute top-4 right-4 bg-success-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center shadow-md">
          <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
          {status}
        </div>
        <div className="absolute bottom-4 left-4 bg-black/40 backdrop-blur-sm text-white px-3 py-2 rounded-lg text-sm font-medium">
          <div className="font-bold text-lg">{petName}</div>
          <div className="text-xs opacity-90">{petType}</div>
        </div>
      </div>
      <div className="mt-4 p-4 bg-white/20 backdrop-blur-sm rounded-lg">
        <div className="flex justify-between text-sm mb-3">
          <div className="font-medium text-white">{petName}</div>
          <div className="pet-type-icon px-2 py-1 rounded-md bg-secondary-500/80 text-white text-xs font-medium backdrop-blur-sm border border-white/20">
            {caregiverName}
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-primary-200 flex items-center justify-center mr-2">
              <SafeIcon icon={FiCalendar} className="text-primary-700" />
            </div>
            <div className="text-xs">
              <div className="text-white/80">{t('home.nextFeeding')}</div>
              <div className="font-medium text-white">{nextFeeding}</div>
            </div>
          </div>
          <div className="flex items-center">
            <div className="w-8 h-8 rounded-full bg-accent-200 flex items-center justify-center mr-2">
              <SafeIcon icon={FiMapPin} className="text-accent-700" />
            </div>
            <div className="text-xs">
              <div className="text-white/80">{t('home.location')}</div>
              <div className="font-medium text-white">{t('home.home')}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PetCarePreviewCard.displayName = 'PetCarePreviewCard';

// 統計卡片組件
const StatsCard = React.memo(({ icon, value, label }) => (
  <motion.div 
    className="bg-white p-4 md:p-6 rounded-xl shadow-smooth border border-neutral-100 text-center transform transition-transform duration-300 hover:shadow-card hover:-translate-y-1" 
    variants={fadeIn}
  >
    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-3">
      <SafeIcon icon={icon} className="text-xl text-primary-600" />
    </div>
    <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">{value}</div>
    <p className="text-sm md:text-base text-neutral-600">{label}</p>
  </motion.div>
));

StatsCard.displayName = 'StatsCard';

const HomePage = () => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('owners');
  const [activePet, setActivePet] = useState(0);

  // 使用 ref 追蹤滾動動畫
  const heroRef = useRef(null);
  const statsRef = useRef(null);
  const howItWorksRef = useRef(null);
  const featuresRef = useRef(null);
  const appPreviewRef = useRef(null);
  const testimonialsRef = useRef(null);

  // 檢查元素是否在視窗內
  const heroInView = useInView(heroRef, { once: true, margin: "-50px" });
  const statsInView = useInView(statsRef, { once: true, margin: "-50px" });
  const howItWorksInView = useInView(howItWorksRef, { once: true, margin: "-50px" });
  const featuresInView = useInView(featuresRef, { once: true, margin: "-50px" });
  const appPreviewInView = useInView(appPreviewRef, { once: true, margin: "-50px" });
  const testimonialsInView = useInView(testimonialsRef, { once: true, margin: "-50px" });

  // 使用 useMemo 快取預覽寵物數據
  const previewPets = useMemo(() => [
    {
      name: 'Max',
      type: t('home.corgi'),
      image: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      status: t('home.healthyStatus'),
      nextFeeding: '18:00',
      caregiverName: t('home.caregiver') + ': Emma'
    },
    {
      name: 'Luna',
      type: t('home.siameseCat'),
      image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      status: t('home.healthyStatus'),
      nextFeeding: '19:30',
      caregiverName: t('home.caregiver') + ': Michael'
    },
    {
      name: 'Charlie',
      type: t('home.borderCollie'),
      image: 'https://images.unsplash.com/photo-1503256207526-0d5d80fa2f47?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80',
      status: t('home.healthyStatus'),
      nextFeeding: '17:15',
      caregiverName: t('home.caregiver') + ': Sarah'
    }
  ], [t]);

  // 使用 useMemo 快取頭像配置
  const avatars = useMemo(() => getTestimonialAvatars(), []);

  // 使用 useMemo 快取推薦數據
  const testimonials = useMemo(() => [
    {
      quote: t('home.testimonial1'),
      author: t('home.testimonial1Author'),
      role: t('home.testimonial1Role'),
      image: avatars[0].url
    },
    {
      quote: t('home.testimonial2'),
      author: t('home.testimonial2Author'),
      role: t('home.testimonial2Role'),
      image: avatars[1].url
    },
    {
      quote: t('home.testimonial3'),
      author: t('home.testimonial3Author'),
      role: t('home.testimonial3Role'),
      image: avatars[2].url
    },
    {
      quote: t('home.testimonial4'),
      author: t('home.testimonial4Author'),
      role: t('home.testimonial4Role'),
      image: avatars[3].url
    },
    {
      quote: t('home.testimonial5'),
      author: t('home.testimonial5Author'),
      role: t('home.testimonial5Role'),
      image: avatars[4].url
    },
    {
      quote: t('home.testimonial6'),
      author: t('home.testimonial6Author'),
      role: t('home.testimonial6Role'),
      image: avatars[5].url
    }
  ], [t, avatars]);

  // 優化自動切換邏輯
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePet((prev) => (prev + 1) % previewPets.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [previewPets.length]);

  // 使用 useCallback 優化點擊處理
  const handlePetChange = useCallback((index) => {
    setActivePet(index);
  }, []);

  // Structured Data for Homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Pet Care Note",
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "ratingCount": "328",
      "reviewCount": "187"
    }
  };

  return (
    <>
      <SEO 
        title={t('common.seo.home.title')}
        description={t('common.seo.home.description')}
        keywords={t('common.seo.home.keywords')}
        structuredData={structuredData}
      />
      <div className="min-h-screen">
        {/* Hero Section */}
        <section ref={heroRef} className="relative overflow-hidden bg-gradient-to-br from-primary-600 via-primary-700 to-secondary-800 text-white pt-12 pb-16">
          <div className="absolute inset-0 paw-pattern-bg opacity-10"></div>
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-600/90 via-primary-700/70 to-secondary-800/80"></div>
          </div>

          <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
            <div className="flex flex-col md:flex-row items-center justify-between gap-12 pt-4">
              <motion.div
                className="md:w-1/2 mb-10 md:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                <span className="inline-block px-4 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium mb-6">
                  {t('home.appSubtitle')}
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-white text-shadow-lg">
                  {t('home.appTitle')}
                </h1>
                <p className="text-xl mb-8 text-blue-50 max-w-lg">
                  {t('home.createPetProfileDesc')}
                </p>
                <div className="flex flex-wrap gap-4">
                  <LocalizedLink to="/dashboard" className="btn btn-accent btn-lg">
                    {t('home.getStarted')}
                  </LocalizedLink>
                  <LocalizedLink to="/features" className="btn btn-white btn-lg">
                    {t('home.learnMore')}
                  </LocalizedLink>
                </div>
              </motion.div>

              <motion.div
                className="md:w-1/2 flex flex-col items-center"
                initial={{ opacity: 0, x: 30 }}
                animate={heroInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
                transition={{ duration: 0.5, delay: 0.15, ease: "easeOut" }}
              >
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePet}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <PetCarePreviewCard
                      petName={previewPets[activePet].name}
                      petType={previewPets[activePet].type}
                      image={previewPets[activePet].image}
                      status={previewPets[activePet].status}
                      nextFeeding={previewPets[activePet].nextFeeding}
                      caregiverName={previewPets[activePet].caregiverName}
                    />
                  </motion.div>
                </AnimatePresence>

                {/* 改進的寵物切換按鈕 - 移除名稱標籤 */}
                <div className="flex justify-center items-center mt-6 space-x-3 bg-white/10 backdrop-blur-md rounded-full px-4 py-3 shadow-lg">
                  {previewPets.map((pet, index) => (
                    <button
                      key={index}
                      onClick={() => handlePetChange(index)}
                      className={`group relative transition-all duration-300 ${
                        activePet === index
                          ? 'w-12 h-4'
                          : 'w-4 h-4'
                      }`}
                      aria-label={`View ${pet.name}`}
                      title={pet.name}
                    >
                      <div
                        className={`absolute inset-0 rounded-full transition-all duration-300 ${
                          activePet === index
                            ? 'bg-white shadow-md'
                            : 'bg-white/50 group-hover:bg-white/70 group-hover:scale-110'
                        }`}
                      ></div>
                      {activePet === index && (
                        <motion.div
                          layoutId="activePetIndicator"
                          className="absolute inset-0 rounded-full bg-gradient-to-r from-accent-400 to-accent-500 shadow-lg"
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" className="fill-white">
              <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
            </svg>
          </div>
        </section>

        {/* Stats Section */}
        <section ref={statsRef} className="py-10">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8"
              initial="hidden"
              animate={statsInView ? "visible" : "hidden"}
              variants={stagger}
            >
              <StatsCard icon={FiHeart} value="10K+" label={t('home.happyPets')} />
              <StatsCard icon={FiUsers} value="5K+" label={t('home.usersFamilies')} />
              <StatsCard icon={FiClipboard} value="15K+" label={t('home.careProfiles')} />
              <StatsCard icon={FiCheckCircle} value="98%" label={t('home.satisfactionRate')} />
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" ref={howItWorksRef} className="py-20 bg-gradient-to-br from-neutral-50 to-primary-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent"></div>
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-100 rounded-full opacity-50 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-secondary-100 rounded-full opacity-50 blur-3xl"></div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0 }}
              animate={howItWorksInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-800 text-sm font-medium mb-4">
                {t('home.easiestWay')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('home.howItWorks')}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-primary-600 to-secondary-600 mx-auto mb-6"></div>
              <p className="max-w-2xl mx-auto text-neutral-600 text-lg">{t('home.howItWorksSubtitle')}</p>

              <div className="flex justify-center mt-8">
                <div className="inline-flex rounded-lg p-1">
                  <button
                    className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'owners'
                        ? 'bg-primary-500 text-white'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                    onClick={() => setActiveTab('owners')}
                  >
                    {t('home.forPetOwners')}
                  </button>
                  <button
                    className={`px-5 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${
                      activeTab === 'caregivers'
                        ? 'bg-secondary-500 text-white'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    }`}
                    onClick={() => setActiveTab('caregivers')}
                  >
                    {t('home.forCaregivers')}
                  </button>
                </div>
              </div>
            </motion.div>

            <div className="relative">
              <TabContent isActive={activeTab === 'owners'}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <StepCard number="1" title={t('home.createPetProfile')} description={t('home.createPetProfileDesc')} icon={FiClipboard} delay={0.1} />
                  <StepCard number="2" title={t('home.shareWithCaregivers')} description={t('home.shareWithCaregiversDesc')} icon={FiShare2} delay={0.2} />
                  <StepCard number="3" title={t('home.travelWithPeace')} description={t('home.travelWithPeaceDesc')} icon={FiHeart} delay={0.3} />
                </div>
              </TabContent>

              <TabContent isActive={activeTab === 'caregivers'}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
                  <StepCard number="1" title={t('home.accessPetDetails')} description={t('home.accessPetDetailsDesc')} icon={FiCheckCircle} delay={0.1} />
                  <StepCard number="2" title={t('home.followSchedules')} description={t('home.followSchedulesDesc')} icon={FiCalendar} delay={0.2} />
                  <StepCard number="3" title={t('home.stayConnected')} description={t('home.stayConnectedDesc')} icon={FiMessageCircle} delay={0.3} />
                </div>
              </TabContent>
            </div>

            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={howItWorksInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <LocalizedLink to="/dashboard" className="btn btn-primary btn-lg">
                {t('home.getStarted')}
              </LocalizedLink>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section ref={featuresRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0 }}
              animate={featuresInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-secondary-100 text-secondary-800 text-sm font-medium mb-4">
                {t('home.whyChooseUs')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('home.features')}</h2>
              <div className="w-20 h-1 bg-gradient-to-r from-secondary-600 to-primary-600 mx-auto mb-6"></div>
              <p className="max-w-2xl mx-auto text-neutral-600 text-lg">{t('home.featuresSubtitle')}</p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="hidden"
              animate={featuresInView ? "visible" : "hidden"}
              variants={stagger}
            >
              <FeatureCard icon={FiClipboard} title={t('home.featureTitle1')} description={t('home.featureDesc1')} delay={0.1} />
              <FeatureCard icon={FiLock} title={t('home.featureTitle2')} description={t('home.featureDesc2')} delay={0.2} />
              <FeatureCard icon={FiShare2} title={t('home.featureTitle3')} description={t('home.featureDesc3')} delay={0.3} />
              <FeatureCard icon={FiCalendar} title={t('home.scheduleFeature')} description={t('home.scheduleFeatureDesc')} delay={0.4} />
              <FeatureCard icon={FiHeart} title={t('home.healthFeature')} description={t('home.healthFeatureDesc')} delay={0.5} />
              <FeatureCard icon={FiSmartphone} title={t('home.mobileFeature')} description={t('home.mobileFeatureDesc')} delay={0.6} />
            </motion.div>

            {/* Internal Link to Features Page */}
            <motion.div
              className="text-center mt-12"
              initial={{ opacity: 0, y: 20 }}
              animate={featuresInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: 0.7 }}
            >
              <LocalizedLink 
                to="/features" 
                className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium group"
              >
                {t('home.viewAllFeatures')}
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </LocalizedLink>
            </motion.div>
          </div>
        </section>

        {/* App Preview Section */}
        <section ref={appPreviewRef} className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-20 bg-gradient-to-b from-white to-transparent"></div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial="hidden"
                animate={appPreviewInView ? "visible" : "hidden"}
                variants={slideRight}
              >
                <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-800 text-sm font-medium mb-4">
                  {t('home.seamlessExperience')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-neutral-800">{t('home.appPreviewTitle')}</h2>
                <p className="text-neutral-600 text-lg mb-8">
                  {t('home.appPreviewDescription')}
                </p>

                <div className="space-y-6">
                  <motion.div className="flex items-start" variants={fadeIn} custom={0.1}>
                    <div className="bg-white p-3 rounded-full shadow-md mr-4 mt-1">
                      <SafeIcon icon={FiUsers} className="text-xl text-primary-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-800 mb-1">{t('home.multiUserAccess')}</h3>
                      <p className="text-neutral-600">{t('home.multiUserAccessDesc')}</p>
                    </div>
                  </motion.div>

                  <motion.div className="flex items-start" variants={fadeIn} custom={0.2}>
                    <div className="bg-white p-3 rounded-full shadow-md mr-4 mt-1">
                      <SafeIcon icon={FiTrendingUp} className="text-xl text-secondary-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-800 mb-1">{t('home.healthTracking')}</h3>
                      <p className="text-neutral-600">{t('home.healthTrackingDesc')}</p>
                    </div>
                  </motion.div>

                  <motion.div className="flex items-start" variants={fadeIn} custom={0.3}>
                    <div className="bg-white p-3 rounded-full shadow-md mr-4 mt-1">
                      <SafeIcon icon={FiStar} className="text-xl text-accent-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-neutral-800 mb-1">{t('home.premiumFeature')}</h3>
                      <p className="text-neutral-600">{t('home.premiumFeatureDesc')}</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              <motion.div
                className="flex justify-center"
                initial="hidden"
                animate={appPreviewInView ? "visible" : "hidden"}
                variants={slideLeft}
              >
                <div className="relative">
                  <div className="bg-white rounded-2xl shadow-lg p-5 border border-neutral-200 relative z-10 max-w-md">
                    <div className="bg-neutral-100 rounded-lg p-2 mb-4 flex justify-between">
                      <div className="text-sm font-medium text-neutral-600">{t('home.petDashboard')}</div>
                      <div className="flex">
                        <div className="w-3 h-3 rounded-full bg-danger-500 mx-0.5"></div>
                        <div className="w-3 h-3 rounded-full bg-warning-500 mx-0.5"></div>
                        <div className="w-3 h-3 rounded-full bg-success-500 mx-0.5"></div>
                      </div>
                    </div>

                    <div className="flex items-center mb-6">
                      <img 
                        src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80"
                        alt="Bella - Golden Retriever pet dashboard example showing comprehensive health tracking interface"
                        className="w-16 h-16 rounded-full object-cover border-4 border-primary-100"
                        loading="lazy"
                      />
                      <div className="ml-4">
                        <h3 className="font-bold text-xl text-neutral-800">Bella</h3>
                        <div className="text-sm text-neutral-500">{t('home.dog')} • 3 {t('home.years')}</div>
                      </div>
                      <div className="ml-auto">
                        <span className="badge badge-success">{t('home.healthyStatus')}</span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div className="bg-primary-50 p-3 rounded-lg border border-primary-100">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-primary-100 mr-3">
                            <SafeIcon icon={FiCalendar} className="text-primary-600" />
                          </div>
                          <div>
                            <div className="text-xs text-primary-700 mb-1">{t('home.nextFeeding')}</div>
                            <div className="font-semibold text-neutral-800">18:30</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-secondary-50 p-3 rounded-lg border border-secondary-100">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-secondary-100 mr-3">
                            <SafeIcon icon={FiClock} className="text-secondary-600" />
                          </div>
                          <div>
                            <div className="text-xs text-secondary-700 mb-1">{t('home.nextMedication')}</div>
                            <div className="font-semibold text-neutral-800">20:00</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-accent-50 p-3 rounded-lg border border-accent-100">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-accent-100 mr-3">
                            <SafeIcon icon={FiActivity} className="text-accent-600" />
                          </div>
                          <div>
                            <div className="text-xs text-accent-700 mb-1">{t('home.weight')}</div>
                            <div className="font-semibold text-neutral-800">4.5 kg</div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-success-50 p-3 rounded-lg border border-success-100">
                        <div className="flex items-center">
                          <div className="p-2 rounded-full bg-success-100 mr-3">
                            <SafeIcon icon={FiShield} className="text-success-600" />
                          </div>
                          <div>
                            <div className="text-xs text-success-700 mb-1">{t('home.nextVetVisit')}</div>
                            <div className="font-semibold text-neutral-800">Jul 15</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-1">
                        <div className="text-sm font-medium text-neutral-800 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className="text-primary-500 mr-1" />
                          {t('home.profileCompletion')}
                        </div>
                        <div className="text-xs text-primary-600 font-medium">85%</div>
                      </div>
                      <div className="h-2 bg-neutral-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                          style={{ width: '85%' }}
                        ></div>
                      </div>
                    </div>

                    <div className="flex justify-between">
                      <div className="btn btn-outline-secondary btn-sm opacity-60 cursor-default pointer-events-none">
                        <SafeIcon icon={FiEye} className="mr-1" />
                        {t('home.viewDetails')}
                      </div>
                      <div className="btn btn-primary btn-sm opacity-60 cursor-default pointer-events-none">
                        <SafeIcon icon={FiSettings} className="mr-1" />
                        {t('home.updateProfile')}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section ref={testimonialsRef} className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0 }}
              animate={testimonialsInView ? { opacity: 1 } : { opacity: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block px-4 py-1 rounded-full bg-success-100 text-success-800 text-sm font-medium mb-4">
                {t('home.happyUsers')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-neutral-800">{t('home.testimonials')}</h2>
              <div className="w-20 h-1 bg-success-600 mx-auto mb-6"></div>
              <p className="max-w-2xl mx-auto text-neutral-600 text-lg">{t('home.testimonialsSubtitle')}</p>
            </motion.div>

            <motion.div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
              initial="hidden"
              animate={testimonialsInView ? "visible" : "hidden"}
              variants={stagger}
            >
              {testimonials.map((testimonial, index) => (
                <TestimonialCard
                  key={index}
                  quote={testimonial.quote}
                  author={testimonial.author}
                  role={testimonial.role}
                  image={testimonial.image}
                  delay={index * 0.05}
                />
              ))}
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-primary-600 to-secondary-700 text-white relative overflow-hidden">
          <div className="absolute inset-0 paw-pattern-bg opacity-10"></div>
          <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="max-w-3xl mx-auto"
            >
              <span className="inline-block px-4 py-1 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-medium mb-6">
                {t('home.readyToStart')}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-shadow">{t('home.ctaTitle')}</h2>
              <p className="text-lg text-blue-50 mb-8">{t('home.ctaDescription')}</p>
              <div className="flex flex-wrap justify-center gap-4">
                <LocalizedLink to="/dashboard" className="btn btn-accent btn-lg">
                  {t('home.getStarted')}
                </LocalizedLink>
                <LocalizedLink to="/auth" className="btn btn-white btn-lg">
                  {t('home.signUp')}
                </LocalizedLink>
              </div>

              {/* Internal Links */}
              <div className="mt-12 pt-8 border-t border-white/20">
                <p className="text-blue-100 mb-4">{t('home.learnMoreAbout')}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <LocalizedLink 
                    to="/about" 
                    className="text-white hover:text-blue-200 underline underline-offset-4 transition-colors"
                  >
                    {t('home.aboutUs')}
                  </LocalizedLink>
                  <span className="text-blue-200">•</span>
                  <LocalizedLink 
                    to="/contact" 
                    className="text-white hover:text-blue-200 underline underline-offset-4 transition-colors"
                  >
                    {t('home.contactSupport')}
                  </LocalizedLink>
                  <span className="text-blue-200">•</span>
                  <LocalizedLink 
                    to="/privacy" 
                    className="text-white hover:text-blue-200 underline underline-offset-4 transition-colors"
                  >
                    {t('home.privacyPolicy')}
                  </LocalizedLink>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default HomePage;